/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {SensorType} from '@ion/core/src/types/entities';
import {SensorUnitType} from '@ion/core/src/types/general';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {SensorTypeIcon} from '@ion/dls/src/icons/SensorTypeIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import DeviceStatus from '../status/DeviceStatus';
import {getCurrentStatusTimeStyles} from './statusTimeStyles';
import {getSensorDataAttributes} from './utils';

export interface ShockSensorStatusProps {
  data: {
    id?: string,
    data?: any[],
    threshold?: {
      max?: number,
      min?: number;
    },
  }
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {label, valueText, tile} = getAssetDetailsContentStyles();
  return {
    statusContainer : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'space-between',
      padding        : '10px 15px 0'
    },
    thresholdContainer : {
      color          : dlsColor('grey3'),
      textTransform  : 'uppercase',
      letterSpacing  : '1px',
      display        : 'flex',
      flexDirection  : 'column',
      justifyContent : 'space-between',
      height         : '42px'
    },
    statusText : {
      fontSize   : '20px',
      color      : dlsColor('caRed'),
      paddingTop : '4px'
    },
    sensorIcon : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    icon : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      marginBottom   : '4px'
    },
    currentTime : {
      fontFamily     : 'Roboto',
      fontSize       : '10px',
      fontWeight     : 700,
      letterSpacing  : '1px',
      textTransform  : 'uppercase',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'flex-end',
      padding        : '0 15px',
      color          : dlsColor('caRed')
    },
    currentThreshold : {
      ...dlsTypography('selectionsLabel'),
      color      : dlsColor('caBlue'),
      whiteSpace : 'nowrap'
    },
    // current theme renders the status row as the shared bordered tile and
    // maps its typography to the shared label/value metrics - the red/blue
    // semantic colors stay inline/legacy-class driven, unchanged ...
    currentStatusContainer : {
      ...tile,
      padding : '8px'
    },
    currentThresholdContainer : {
      ...label,
      height : 'auto'
    },
    currentStatusText : {
      ...valueText,
      paddingTop : 0,
      color      : dlsColor('caRed')
    },
    currentTimeText : getCurrentStatusTimeStyles()
  };
});

const ShockSensorStatus = ({data}: ShockSensorStatusProps) => {
  const classes      = useStyles();
  const isCurrent    = isCurrentTheme();
  const unit         = SensorUnitType.GForce;
  const alert        = translate('tracking:deviceStatus.alert');
  const iconType     = SensorType.Shock;
  const maxImpact    = data?.threshold?.max;
  const {
    y: currentValue,
    eventTime: currentTime
  } = getSensorDataAttributes(data);

  return (
    <>
      <div className={themedClassName(classes.statusContainer, classes.currentStatusContainer, isCurrent)}>
        <div className={themedClassName(classes.thresholdContainer, classes.currentThresholdContainer, isCurrent)}>
          <DeviceStatus color={dlsColor('caRed')} text={alert} />
          <span className={classes.currentThreshold}>
            {translate('tracking:scShockSensor.threshold')}&nbsp;
            {maxImpact}{unit}
          </span>
        </div>
        <div>
          <div className={classes.icon}>
            <SensorTypeIcon sensorType={iconType} color={dlsColor('caRed')} size={32}/>
            <span className={themedClassName(classes.statusText, classes.currentStatusText, isCurrent)}>
              {currentValue} {unit}
            </span>
          </div>
        </div>
      </div>
      <span className={themedClassName(classes.currentTime, classes.currentTimeText, isCurrent)}>
        {getFormattedDate(currentTime)}
      </span>
    </>
  );
};

export default ShockSensorStatus;
